import { useEffect, useMemo, useState } from 'react';

import { InsertInvitation as CalendarIcon, ClearOutlined as ClearIcon } from '@mui/icons-material';
import { Box, Button, IconButton, InputAdornment, Popover, TextField, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { StaticDateTimePicker } from '@mui/x-date-pickers/StaticDateTimePicker';
import moment, { Moment } from 'moment';

const DATETIME_FORMAT = 'YYYY-MM-DD HH:mm';
const DATE_FORMAT = 'YYYY-MM-DD';

const useDateTimeFilterControlStyles = makeStyles<Theme>(theme => ({
  clear: {
    color: theme.palette.grey[500],
    opacity: 0.35,
    '&:hover': {
      opacity: 1,
      transition: 'opacity 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    },
  },
}));

const formatFilterValue = (value: unknown, pickerType: 'date' | 'datetime'): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const format = pickerType === 'datetime' ? DATETIME_FORMAT : DATE_FORMAT;

  if (typeof value === 'string') {
    const strict = moment(value, format, true);
    if (strict.isValid()) {
      return strict.format(format);
    }
    const loose = moment(value);
    return loose.isValid() ? loose.format(format) : value;
  }

  const parsed = moment(value);
  return parsed.isValid() ? parsed.format(format) : '';
};

const parsePickerValue = (value: unknown, pickerType: 'date' | 'datetime'): Moment | null => {
  const formatted = formatFilterValue(value, pickerType);
  return formatted
    ? moment(formatted, pickerType === 'datetime' ? DATETIME_FORMAT : DATE_FORMAT, true)
    : null;
};

const applyDayBoundary = (date: Moment, dayBoundary: 'start' | 'end' | undefined): Moment => {
  if (dayBoundary === 'start') {
    return date.clone().startOf('day');
  }
  if (dayBoundary === 'end') {
    return date.clone().endOf('day');
  }
  return date;
};

const formatPickerSelection = (date: Moment, pickerType: 'date' | 'datetime'): string =>
  date.format(pickerType === 'datetime' ? DATETIME_FORMAT : DATE_FORMAT);

interface DateTimeFilterControlProps {
  label: string;
  filterKey: string;
  value: unknown;
  onFilterChange: (condition: string, value: string) => void;
  pickerType: 'date' | 'datetime';
  className: string;
  restriction?: 'min' | 'max';
  restrictionValue?: unknown;
  dayBoundary?: 'start' | 'end';
  disablePast?: boolean;
}

export const DateTimeFilterControl = ({
  filterKey,
  label,
  value,
  onFilterChange,
  className,
  pickerType,
  restriction,
  restrictionValue,
  dayBoundary,
  disablePast,
}: DateTimeFilterControlProps) => {
  const classes = useDateTimeFilterControlStyles();
  const format = pickerType === 'datetime' ? DATETIME_FORMAT : DATE_FORMAT;
  const displayValue = formatFilterValue(value, pickerType);

  const [calendarAnchor, setCalendarAnchor] = useState<HTMLElement | null>(null);
  const [draftValue, setDraftValue] = useState<Moment | null>(() =>
    parsePickerValue(value, pickerType),
  );

  const isCalendarOpen = Boolean(calendarAnchor);

  useEffect(() => {
    if (!isCalendarOpen) {
      setDraftValue(parsePickerValue(value, pickerType));
    }
  }, [value, pickerType, isCalendarOpen]);

  const restrictionProps = useMemo(() => {
    const parsedRestriction = restrictionValue
      ? parsePickerValue(restrictionValue, pickerType)
      : null;
    if (!parsedRestriction) {
      return {};
    }
    if (restriction === 'min') {
      return { minDate: parsedRestriction };
    }
    if (restriction === 'max') {
      return { maxDate: parsedRestriction };
    }
    return {};
  }, [restriction, restrictionValue, pickerType]);

  const applyPickerValue = (date: Moment | null) => {
    if (!date || !date.isValid()) {
      return;
    }
    onFilterChange(filterKey, formatPickerSelection(date, pickerType));
  };

  const closeCalendar = () => setCalendarAnchor(null);

  const handleCalendarAccept = () => {
    applyPickerValue(draftValue);
    closeCalendar();
  };

  const handleCalendarToday = () => {
    const today =
      pickerType === 'datetime' ? applyDayBoundary(moment(), dayBoundary ?? 'start') : moment();
    setDraftValue(today);
    applyPickerValue(today);
    closeCalendar();
  };

  const calendarActions = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 2, pb: 2 }}>
      <Button variant="outlined" onClick={handleCalendarToday}>
        Today
      </Button>
      <Button variant="contained" onClick={handleCalendarAccept}>
        OK
      </Button>
    </Box>
  );

  const calendarPicker =
    pickerType === 'datetime' ? (
      <StaticDateTimePicker
        displayStaticWrapperAs="desktop"
        value={draftValue}
        onChange={setDraftValue}
        ampm={false}
        disablePast={disablePast}
        {...restrictionProps}
        slotProps={{
          actionBar: { actions: [] },
        }}
      />
    ) : (
      <StaticDatePicker
        displayStaticWrapperAs="desktop"
        value={draftValue}
        onChange={setDraftValue}
        disablePast={disablePast}
        {...restrictionProps}
        slotProps={{
          actionBar: { actions: [] },
        }}
      />
    );

  return (
    <>
      <TextField
        data-testid="datetime-filter"
        className={className}
        label={label}
        fullWidth
        size="small"
        value={displayValue}
        placeholder={format}
        onChange={event => onFilterChange(filterKey, event.target.value)}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {displayValue ? (
                <IconButton
                  className={classes.clear}
                  onClick={() => onFilterChange(filterKey, '')}
                  size="small"
                  aria-label="Clear"
                >
                  <ClearIcon />
                </IconButton>
              ) : null}
              <IconButton
                size="small"
                aria-label="Open calendar"
                onClick={event => {
                  setDraftValue(parsePickerValue(value, pickerType) ?? moment());
                  setCalendarAnchor(event.currentTarget);
                }}
              >
                <CalendarIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Popover
        open={isCalendarOpen}
        anchorEl={calendarAnchor}
        onClose={closeCalendar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: { sx: { mt: 0.5 } },
        }}
      >
        {calendarPicker}
        {calendarActions}
      </Popover>
    </>
  );
};
